import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import SearchParams, { defaultParams } from "../types/searchParams";
import SearchResults from "../types/searchResults";
import getSearchQueryString from "../helpers/getSearchQueryString";
import ReportSearch from "../types/reportSearch";
import ExcelReport from "../types/excelReport";

interface ReportState {
  reportFilter: SearchParams;
  jobTimeSearchResults?: SearchResults;
  reportStart?: Date | null;
  reportEnd?: Date | null;
  status: "idle" | "loading" | "failed";
  exportStatus: "idle" | "loading" | "failed";
  mobileJobTimeResults?: SearchResults;
}

const initialState: ReportState = {
  reportFilter: {
    ...defaultParams,
    orderBy: "received",
    orderDirection: "Descending",
  },
  jobTimeSearchResults: undefined,
  status: "idle",
  exportStatus: "idle",
};

const buildFormData = (formData: any, data: any, parentKey?: any) => {
  if (
    data &&
    typeof data === "object" &&
    !(data instanceof Date) &&
    !(data instanceof File)
  ) {
    Object.keys(data).forEach((key) => {
      buildFormData(
        formData,
        data[key],
        parentKey ? `${parentKey}[${key}]` : key,
      );
    });
  } else {
    const value = data == null || data === undefined ? null : data;

    if (!!data) {
      formData.append(parentKey, value);
    }
  }

  return formData;
};

const jsonToFormData = (data: any) => {
  const formData = new FormData();
  const newData = buildFormData(formData, data);
  return newData;
};

const blobToFile = (blob: Blob, fileName: string) => {
  const b: any = blob;
  b.lastModifiedDate = new Date();
  b.name = fileName;

  return blob as File;
};

export const searchJobTimes = createAsyncThunk<
  SearchResults,
  { search: SearchParams; start?: Date; end?: Date }
>("report/job-times", async ({ search, start, end }) => {
  let qs = getSearchQueryString(search);
  let date = "";

  function isValidDate(d: any) {
    return d instanceof Date && !isNaN(d.getTime());
  }

  date +=
    !!start && isValidDate(start) ? `&startDate=${start.toISOString()}` : "";
  date += !!end && isValidDate(end) ? `&endDate=${end.toISOString()}` : "";

  const response = await http.get<SearchResults>(
    `/report/jobtimes?${qs}${date}`,
  );
  return response.data;
});

export const excelReport = createAsyncThunk<boolean, ExcelReport>(
  "report/excel",
  async (report: ExcelReport) => {
    const response = await http.post<Blob>(`/report/excel`, report, {
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `SVReport.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return true;
  },
);

export const jobReportExcel = createAsyncThunk<
  boolean,
  { search: SearchParams; start?: Date; end?: Date }
>("report/job-report-excel", async ({ search, start, end }) => {
  let qs = getSearchQueryString(search);
  let date = "";
  function isValidDate(d: any) {
    return d instanceof Date && !isNaN(d.getTime());
  }
  date +=
    !!start && isValidDate(start) ? `&startDate=${start.toISOString()}` : "";
  date += !!end && isValidDate(end) ? `&endDate=${end.toISOString()}` : "";

  const response = await http.get<Blob>(
    `/report/job-report-excel?${qs}${date}`,
    {
      responseType: "blob",
    },
  );
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "SVJobReport.xlsx");
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return true;
});

export const jobReportPdf = createAsyncThunk<
  boolean,
  { search: SearchParams; start?: Date; end?: Date }
>("report/job-report-pdf", async ({ search, start, end }) => {
  let qs = getSearchQueryString(search);
  let date = "";
  function isValidDate(d: any) {
    return d instanceof Date && !isNaN(d.getTime());
  }
  date +=
    !!start && isValidDate(start) ? `&startDate=${start.toISOString()}` : "";
  date += !!end && isValidDate(end) ? `&endDate=${end.toISOString()}` : "";

  const response = await http.get<Blob>(`/report/job-report-pdf?${qs}${date}`, {
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "SVJobReport.pdf");
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return true;
});

export const jobTimesReport = createAsyncThunk<
  boolean,
  {
    reportType: "excel" | "pdf";
    search: SearchParams;
    start?: Date;
    end?: Date;
  }
>("report/job-times/excel", async ({ reportType, search, start, end }) => {
  let qs = getSearchQueryString(search);
  let date = "";

  function isValidDate(d: any) {
    return d instanceof Date && !isNaN(d.getTime());
  }

  date +=
    !!start && isValidDate(start) ? `&startDate=${start.toISOString()}` : "";
  date += !!end && isValidDate(end) ? `&endDate=${end.toISOString()}` : "";

  const response = await http.get<Blob>(
    `/report/jobtimes-${reportType}?${qs}${date}`,
    {
      responseType: "blob",
    },
  );

  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "jobtime-report.xlsx");
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return true;
});

export const emailJobTimeReport = createAsyncThunk<
  null,
  { blob: Blob; type: "excel" | "pdf"; email: string[] }
>("report/job-times/email", async ({ blob, type, email }) => {
  let reportFile;
  if (type === "pdf") {
    reportFile = blobToFile(blob, "export-email.pdf");
  } else {
    reportFile = blobToFile(blob, "export-email.xlsx");
  }

  var form = jsonToFormData(reportFile);

  form.append("upload", reportFile);

  form.append("fileName", reportFile?.name!);

  const config = {
    headers: { "Content-Type": "multipart/form-data" },
  };

  const response = await http.patch(`/report/email-jobtimes`, form, config);
  return response.data;
});

const reportSlice = createSlice({
  name: "report",
  initialState,
  reducers: {
    setReportFilterAll(state, action: PayloadAction<ReportSearch>) {
      state.reportFilter = action.payload.search;
      state.reportStart = action.payload.start;
      state.reportEnd = action.payload.end;
    },
    setReportFilter(state, action) {
      state.reportFilter = action.payload;
    },
    setReportStart(state, action) {
      state.reportStart = action.payload;
    },
    setReportEnd(state, action) {
      state.reportEnd = action.payload;
    },
    resetReportStart(state) {
      state.reportStart = undefined;
    },
    resetReportEnd(state) {
      state.reportEnd = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchJobTimes.pending, (state) => {
        state.status = "loading";
      })
      .addCase(searchJobTimes.fulfilled, (state, action) => {
        state.status = "idle";
        state.jobTimeSearchResults = action.payload;
        state.mobileJobTimeResults =
          state.reportFilter.pageNumber === 1 || !state.mobileJobTimeResults
            ? action.payload
            : {
                ...state.mobileJobTimeResults,
                ...action.payload,
                results: [
                  ...(state.mobileJobTimeResults?.results ?? []),
                  ...action.payload.results,
                ],
              };
      })
      .addCase(searchJobTimes.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(jobTimesReport.pending, (state) => {
        state.status = "loading";
      })
      .addCase(jobTimesReport.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(jobTimesReport.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(jobReportExcel.pending, (state) => {
        state.exportStatus = "loading";
      })
      .addCase(jobReportExcel.fulfilled, (state) => {
        state.exportStatus = "idle";
      })
      .addCase(jobReportExcel.rejected, (state) => {
        state.exportStatus = "failed";
      })
      .addCase(jobReportPdf.pending, (state) => {
        state.exportStatus = "loading";
      })
      .addCase(jobReportPdf.fulfilled, (state) => {
        state.exportStatus = "idle";
      })
      .addCase(jobReportPdf.rejected, (state) => {
        state.exportStatus = "failed";
      });
  },
});

export default reportSlice.reducer;
export const {
  setReportFilterAll,
  setReportFilter,
  setReportStart,
  setReportEnd,
  resetReportStart,
  resetReportEnd,
} = reportSlice.actions;
